/**
 * Numa Dashboard: where the fleet data is read from.
 *
 * Three reads, all in the deployer account, all read-only:
 *
 *   1. The tiered index, `fleet/<tier>.json.gz` in the snapshots bucket. One
 *      small file for every client first (`core`), then heavier files a view
 *      asks for. This is what the dashboard draws from.
 *   2. One client's complete snapshot, `latest/<client>.json` in the same
 *      bucket. A single-client view loads it.
 *   3. The old per-client rows in DynamoDB. Only used when the index does not
 *      exist yet (before its first build). Each row is a cut copy of a
 *      snapshot.
 *
 * The dashboard never writes anything back.
 */
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient, QueryCommand, ScanCommand } from '@aws-sdk/lib-dynamodb';
import { GetObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { awsCredentialsService } from './awsCredentialsService';
import { getAllConfig } from './configService';
import type { FleetTier } from '@/components/NumaDashboard/fleetData';
import type { ClientSnapshot, FleetIndexFile } from '@/types/fleetAnalytics';

/** Sparse index that holds only the latest row of each client (PK `latest_pk = "LATEST"`). */
const LATEST_SNAPSHOTS_INDEX = 'latest-snapshots-index';

/**
 * For testing on localhost before the index is deployed. In a dev build, when
 * `localStorage.FLEET_INDEX_DEV_URL` is set (for example `/_fleet-dev`), the
 * tier files are fetched from that URL in place of S3. A production build
 * never reads the key.
 */
function devIndexUrl(): string | null {
  if (!import.meta.env?.DEV) return null;
  try {
    const url = window.localStorage.getItem('FLEET_INDEX_DEV_URL');
    return url ? url.replace(/\/+$/, '') : null;
  } catch {
    return null;
  }
}

/**
 * S3 says "no such object" in two ways. The portal's role may read objects in
 * the bucket but may not list it, and S3 then answers 403 for a key that does
 * not exist, where a role that can list gets 404.
 */
function isMissingObject(error: unknown): boolean {
  const e = error as { name?: string; $metadata?: { httpStatusCode?: number } } | null;
  const status = e?.$metadata?.httpStatusCode;
  return (
    status === 404 || status === 403 || e?.name === 'NoSuchKey' || e?.name === 'NotFound' || e?.name === 'AccessDenied'
  );
}

/**
 * Bytes to text. A tier file is stored as gzip bytes with no content encoding
 * (a transparent decode by the browser would break the SDK's checksum check),
 * so it is unpacked here. Bytes that are not gzip are read as plain text: a
 * server that did decode on the way is then still understood.
 */
async function bytesToText(bytes: Uint8Array): Promise<string> {
  const gzip = bytes.length > 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
  if (!gzip) return new TextDecoder().decode(bytes);
  const stream = new Blob([bytes as BlobPart]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
}

function isIndexFile(doc: unknown, tier: string): doc is FleetIndexFile {
  const d = doc as Partial<FleetIndexFile> | null;
  return Boolean(d && typeof d === 'object' && d.tier === tier && d.clients && typeof d.clients === 'object');
}

class FleetAnalyticsService {
  private _ddbClient: DynamoDBDocumentClient | null = null;
  private _s3Client: S3Client | null = null;

  private async ddb(): Promise<DynamoDBDocumentClient> {
    if (!this._ddbClient) {
      const credentials = await awsCredentialsService.getDeployerCredentials();
      const region = getAllConfig()?.AWS_REGION ?? 'us-east-1';
      this._ddbClient = DynamoDBDocumentClient.from(
        awsCredentialsService.withRevokedRetry(new DynamoDBClient({ region, credentials })),
        {
          marshallOptions: { removeUndefinedValues: true },
        }
      );
    }
    return this._ddbClient;
  }

  private async s3(): Promise<S3Client> {
    if (!this._s3Client) {
      const credentials = await awsCredentialsService.getDeployerCredentials();
      const region = getAllConfig()?.AWS_REGION ?? 'us-east-1';
      this._s3Client = awsCredentialsService.withRevokedRetry(new S3Client({ region, credentials }));
    }
    return this._s3Client;
  }

  private tableName(): string {
    const t = getAllConfig()?.FLEET_ANALYTICS_TABLE;
    if (!t) throw new Error('FLEET_ANALYTICS_TABLE not configured');
    return t;
  }

  private snapshotsBucket(): string | undefined {
    return getAllConfig()?.FLEET_ANALYTICS_SNAPSHOTS_BUCKET;
  }

  /**
   * One tier file of the fleet index. Returns null when the file does not
   * exist: for `core` that means the index is not built yet and the caller
   * reads the old DynamoDB rows. Any other failure throws.
   */
  async fetchFleetTier(tier: 'core' | FleetTier): Promise<FleetIndexFile | null> {
    const dev = devIndexUrl();
    let bytes: Uint8Array;

    if (dev) {
      const resp = await fetch(`${dev}/${tier}.json.gz`, { cache: 'no-store' });
      if (resp.status === 404 || resp.status === 403) return null;
      if (!resp.ok) throw new Error(`${tier} tier: the dev server answered ${resp.status}`);
      bytes = new Uint8Array(await resp.arrayBuffer());
    } else {
      const bucket = this.snapshotsBucket();
      // No bucket in the portal's config: this deploy has no index.
      if (!bucket) return null;
      try {
        const s3 = await this.s3();
        const resp = await s3.send(new GetObjectCommand({ Bucket: bucket, Key: `fleet/${tier}.json.gz` }));
        if (!resp.Body) return null;
        bytes = await resp.Body.transformToByteArray();
      } catch (e) {
        if (isMissingObject(e)) return null;
        throw e;
      }
    }

    let doc: unknown;
    try {
      doc = JSON.parse(await bytesToText(bytes));
    } catch (e) {
      // The dev server answers a file that is not there with the app's own
      // HTML page and a 200. That is "no file", not a broken index.
      if (dev) return null;
      throw new Error(`${tier} tier: the file could not be read (${e instanceof Error ? e.message : String(e)})`);
    }
    if (!isIndexFile(doc, tier)) {
      if (dev) return null;
      throw new Error(`${tier} tier: the file is not a fleet index file`);
    }
    return doc;
  }

  /**
   * One client's complete snapshot from `latest/<client>.json`. Returns null
   * when the bucket holds none for the client. An old DynamoDB row names its
   * own key (`header.s3_key`); pass it when there is one.
   */
  async fetchFullSnapshot(clientName: string, s3Key?: string): Promise<ClientSnapshot | null> {
    const bucket = this.snapshotsBucket();
    if (!bucket) return null;
    try {
      const s3 = await this.s3();
      const resp = await s3.send(new GetObjectCommand({ Bucket: bucket, Key: s3Key || `latest/${clientName}.json` }));
      const text = await resp.Body?.transformToString();
      if (!text) return null;
      const full = JSON.parse(text) as ClientSnapshot;
      return full?.client ? full : null;
    } catch (e) {
      if (isMissingObject(e)) return null;
      throw e;
    }
  }

  /**
   * The latest row of every client from DynamoDB. The fallback for a deploy
   * with no index yet.
   *
   * It queries the sparse index of latest rows. When that index is not usable
   * (still being created, or not backfilled) it scans the table in parallel
   * segments, which reads every dated row too and is slow.
   */
  async fetchAllSnapshots(): Promise<Record<string, ClientSnapshot>> {
    try {
      const viaGsi = await this.queryLatestSnapshots();
      if (Object.keys(viaGsi).length > 0) return viaGsi;
      console.warn('[fleetAnalytics] latest-snapshots-index returned no rows; falling back to Scan');
    } catch (e) {
      console.warn('[fleetAnalytics] latest-snapshots-index query failed; falling back to Scan', e);
    }
    return this.scanAllSnapshots();
  }

  private async queryLatestSnapshots(): Promise<Record<string, ClientSnapshot>> {
    const client = await this.ddb();
    const out: Record<string, ClientSnapshot> = {};
    let lastKey: Record<string, unknown> | undefined;
    do {
      const resp = await client.send(
        new QueryCommand({
          TableName: this.tableName(),
          IndexName: LATEST_SNAPSHOTS_INDEX,
          KeyConditionExpression: 'latest_pk = :pk',
          ExpressionAttributeValues: { ':pk': 'LATEST' },
          ExclusiveStartKey: lastKey,
        })
      );
      for (const row of resp.Items ?? []) {
        const name = (row as { clientName?: string }).clientName;
        const body = (row as { body?: ClientSnapshot }).body;
        if (name && body) out[name] = body;
      }
      lastKey = resp.LastEvaluatedKey as Record<string, unknown> | undefined;
    } while (lastKey);
    return out;
  }

  private async scanAllSnapshots(): Promise<Record<string, ClientSnapshot>> {
    const client = await this.ddb();
    const TOTAL_SEGMENTS = 12;
    const tableName = this.tableName();

    const scanSegment = async (segment: number): Promise<Array<[string, ClientSnapshot]>> => {
      const collected: Array<[string, ClientSnapshot]> = [];
      let lastKey: Record<string, unknown> | undefined;
      do {
        const resp = await client.send(
          new ScanCommand({
            TableName: tableName,
            FilterExpression: 'sk = :latest',
            ExpressionAttributeValues: { ':latest': 'SNAPSHOT#latest' },
            Segment: segment,
            TotalSegments: TOTAL_SEGMENTS,
            ExclusiveStartKey: lastKey,
          })
        );
        for (const row of resp.Items ?? []) {
          const name = (row as { clientName?: string }).clientName;
          const body = (row as { body?: ClientSnapshot }).body;
          if (name && body) collected.push([name, body]);
        }
        lastKey = resp.LastEvaluatedKey as Record<string, unknown> | undefined;
      } while (lastKey);
      return collected;
    };

    const segments = await Promise.all(Array.from({ length: TOTAL_SEGMENTS }, (_, i) => scanSegment(i)));
    const out: Record<string, ClientSnapshot> = {};
    for (const segment of segments) {
      for (const [name, body] of segment) out[name] = body;
    }
    return out;
  }
}

export const fleetAnalyticsService = new FleetAnalyticsService();
